/**
 * 应用状态管理 Hook
 * 管理全局状态：项目、配置、当前步骤、分析结果、目录等
 * 通过 userId 隔离不同用户的 localStorage 草稿数据
 */
import { useState, useCallback, useMemo } from 'react';
import { AppState, ConfigData, OutlineData, OutlineSource, GenerationMode, ProjectInfo } from '../types';
import { createDraftStorage } from '../utils/draftStorage';

const initialState: AppState = {
  currentStep: 0,
  config: {
    api_key: '',
    base_url: '',
    model_name: 'gpt-3.5-turbo',
    concurrency: 2,
    mermaid_default_view: 'code',
  },
  currentProject: null,
  fileContent: '',
  fileName: '',
  projectOverview: '',
  techRequirements: '',
  analysisResults: {},
  outlineData: null,
  bizOutlineData: null,
  outlineSource: null,
  generationMode: 'tech',
};

export function useAppState(userId: number | undefined) {
  // 为每个用户创建独立的 draftStorage 实例
  const storage = useMemo(() => createDraftStorage(userId ?? 0), [userId]);

  const [state, setState] = useState<AppState>(() => {
    if (!userId) return initialState;
    const saved = storage.loadDraft();
    if (saved) {
      return {
        ...initialState,
        ...saved,
        config: saved.config || initialState.config,
      };
    }
    return initialState;
  });

  const updateConfig = useCallback((config: ConfigData) => {
    setState((prev) => {
      const next = { ...prev, config };
      storage.saveDraft({ config });
      return next;
    });
  }, [storage]);

  const setCurrentProject = useCallback((project: ProjectInfo | null) => {
    setState((prev) => {
      const next = { ...prev, currentProject: project };
      storage.saveDraft({ currentProject: project });
      return next;
    });
  }, [storage]);

  const updateStep = useCallback((step: number) => {
    setState((prev) => {
      const next = { ...prev, currentStep: step };
      storage.saveDraft({ currentStep: step });
      return next;
    });
  }, [storage]);

  const updateFileContent = useCallback((content: string) => {
    setState((prev) => {
      const next = { ...prev, fileContent: content };
      storage.saveDraft({ fileContent: content });
      return next;
    });
  }, [storage]);

  const updateFileName = useCallback((name: string) => {
    setState((prev) => {
      const next = { ...prev, fileName: name };
      storage.saveDraft({ fileName: name });
      return next;
    });
  }, [storage]);

  const updateAnalysisResults = useCallback(
    (projectOverview: string, _techScoring: string, techRequirements: string) => {
      setState((prev) => {
        // techRequirements 保留用于目录生成等旧逻辑兼容
        const next = { ...prev, projectOverview, techRequirements };
        storage.saveDraft({ projectOverview, techRequirements });
        return next;
      });
    },
    [storage]
  );

  /** 保存全部解析结果（覆盖） */
  const setAnalysisResults = useCallback(
    (results: Record<string, string>) => {
      setState((prev) => {
        const next = { ...prev, analysisResults: results };
        storage.saveDraft({ analysisResults: results });
        return next;
      });
    },
    [storage]
  );

  /** 更新单个解析结果 */
  const updateAnalysisResultItem = useCallback(
    (analysisType: string, content: string) => {
      setState((prev) => {
        const next = {
          ...prev,
          analysisResults: { ...prev.analysisResults, [analysisType]: content },
        };
        storage.saveDraft({ analysisResults: next.analysisResults });
        return next;
      });
    },
    [storage]
  );

  const updateOutline = useCallback((outlineData: OutlineData, source?: OutlineSource) => {
    setState((prev) => {
      const next = { ...prev, outlineData };
      // 如果 outlineData 包含 project_overview 且当前 projectOverview 为空，同步填充
      if (outlineData.project_overview && !prev.projectOverview.trim()) {
        next.projectOverview = outlineData.project_overview;
      }
      if (source) {
        next.outlineSource = source;
      }
      storage.saveDraft({ outlineData, projectOverview: next.projectOverview, outlineSource: next.outlineSource });
      return next;
    });
  }, [storage]);

  const updateOutlineSource = useCallback((source: OutlineSource | null) => {
    setState((prev) => {
      const next = { ...prev, outlineSource: source };
      storage.saveDraft({ outlineSource: source });
      return next;
    });
  }, [storage]);

  const updateBizOutline = useCallback((bizOutlineData: OutlineData | null) => {
    setState((prev) => {
      const next = { ...prev, bizOutlineData };
      storage.saveDraft({ bizOutlineData });
      return next;
    });
  }, [storage]);

  const setGenerationMode = useCallback((mode: GenerationMode) => {
    setState((prev) => {
      const next = { ...prev, generationMode: mode };
      storage.saveDraft({ generationMode: mode });
      return next;
    });
  }, [storage]);

  const nextStep = useCallback(() => {
    setState((prev) => {
      if (prev.currentStep >= 2) return prev;
      const nextStep = prev.currentStep + 1;
      const next = { ...prev, currentStep: nextStep };
      storage.saveDraft({ currentStep: nextStep });
      return next;
    });
  }, [storage]);

  const prevStep = useCallback(() => {
    setState((prev) => {
      if (prev.currentStep <= 0) return prev;
      const prevStepIdx = prev.currentStep - 1;
      const next = { ...prev, currentStep: prevStepIdx };
      storage.saveDraft({ currentStep: prevStepIdx });
      return next;
    });
  }, [storage]);

  const resetState = useCallback(() => {
    setState(initialState);
    storage.clearAll();
  }, [storage]);

  return {
    state,
    updateConfig,
    setCurrentProject,
    updateStep,
    updateFileContent,
    updateFileName,
    updateAnalysisResults,
    setAnalysisResults,
    updateAnalysisResultItem,
    updateOutline,
    updateBizOutline,
    updateOutlineSource,
    setGenerationMode,
    nextStep,
    prevStep,
    resetState,
  };
}
